import React, { useState } from 'react';
import { 
  ChevronDown, 
  MoreVertical, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  Keyboard,
  Settings,
  Inbox,
  Users,
  Tag,
  Square,
  CheckSquare,
  Loader2,
  Clock,
  Zap
} from 'lucide-react';
import GmailEmailRow from './GmailEmailRow';

const Section = ({ Icon, title, color, selected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`gmail-section ${selected ? 'gmail-section--selected' : ''}`}
      style={{
        borderBottomColor: selected ? color : 'transparent',
        color: selected ? color : '#5f6368'
      }}
    >
      <Icon size={18} style={{ color: selected ? color : '#5f6368' }} />
      <span>{title}</span>
    </button>
  );
};

const GmailEmailList = ({ 
  emails, 
  loading, 
  syncing,
  fromCache,
  lastSync,
  onSelectMail, 
  onToggleStar,
  onRefresh,
  activeSection 
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState('primary');

  const handleSelectAll = () => {
    if (selectedIds.length === emails.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(emails.map(e => e.id));
    }
  };

  const handleSelectOne = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Filter emails based on active tab
  const filteredEmails = emails.filter(email => {
    if (activeTab === 'primary') {
      return !email.labels?.includes('SOCIAL') && !email.labels?.includes('PROMOTIONS');
    }
    if (activeTab === 'social') {
      return email.labels?.includes('SOCIAL');
    }
    if (activeTab === 'promotions') {
      return email.labels?.includes('PROMOTIONS');
    }
    return true;
  });

  // Format last sync time
  const formatLastSync = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="gmail-email-list" data-testid="gmail-email-list">
      {/* Toolbar */}
      <div className="gmail-email-list-settings">
        <div className="gmail-email-list-settings-left">
          <button 
            className="gmail-checkbox-btn"
            onClick={handleSelectAll}
          >
            {selectedIds.length === emails.length && emails.length > 0 ? (
              <CheckSquare size={18} />
            ) : (
              <Square size={18} />
            )}
          </button>
          <button className="gmail-icon-btn">
            <ChevronDown size={18} />
          </button>
          <button 
            className={`gmail-icon-btn ${syncing ? 'gmail-icon-btn--syncing' : ''}`} 
            onClick={onRefresh} 
            data-testid="gmail-refresh-btn"
            disabled={loading || syncing}
            title={syncing ? 'Syncing...' : 'Refresh'}
          >
            <RefreshCw size={18} className={syncing ? 'gmail-spin' : ''} />
          </button>
          <button className="gmail-icon-btn">
            <MoreVertical size={18} />
          </button>
          
          {/* Cache/Sync status indicator */}
          {!loading && emails.length > 0 && (
            <div className="gmail-sync-status" data-testid="gmail-sync-status">
              {fromCache ? (
                <span className="gmail-cache-badge" title="Loaded from cache - checking for updates...">
                  <Zap size={14} />
                  Instant load
                </span>
              ) : syncing ? (
                <span className="gmail-syncing-badge">
                  <Loader2 size={14} className="gmail-spin" />
                  Syncing...
                </span>
              ) : lastSync ? (
                <span className="gmail-synced-badge" title={`Last synced: ${lastSync}`}>
                  <Clock size={14} />
                  {formatLastSync(lastSync)}
                </span>
              ) : null}
            </div>
          )}
        </div>
        <div className="gmail-email-list-settings-right">
          <span className="gmail-pagination-text">
            1-{emails.length} of {emails.length}
          </span>
          <button className="gmail-icon-btn">
            <ChevronLeft size={18} />
          </button>
          <button className="gmail-icon-btn">
            <ChevronRight size={18} />
          </button>
          <button className="gmail-icon-btn">
            <Keyboard size={18} />
          </button>
          <button className="gmail-icon-btn">
            <Settings size={18} />
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="gmail-email-list-sections">
        <Section 
          Icon={Inbox} 
          title="Primary" 
          color="#d93025"
          selected={activeTab === 'primary'}
          onClick={() => setActiveTab('primary')}
        />
        <Section 
          Icon={Users} 
          title="Social" 
          color="#1a73e8"
          selected={activeTab === 'social'}
          onClick={() => setActiveTab('social')}
        />
        <Section 
          Icon={Tag} 
          title="Promotions" 
          color="#188038"
          selected={activeTab === 'promotions'}
          onClick={() => setActiveTab('promotions')}
        />
      </div>

      {/* Email List */}
      <div className="gmail-email-list-content">
        {loading ? (
          <div className="gmail-loading">
            <Loader2 className="gmail-loading-spinner" size={32} />
            <span>Loading emails...</span>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="gmail-empty">
            <Inbox size={64} className="gmail-empty-icon" />
            <h3>No emails found</h3>
            <p>Looks like your {activeTab} inbox is empty</p>
          </div>
        ) : (
          filteredEmails.map(email => (
            <GmailEmailRow
              key={email.id}
              email={email}
              selected={selectedIds.includes(email.id)}
              onSelect={(e) => handleSelectOne(email.id, e)}
              onClick={() => onSelectMail(email)}
              onToggleStar={(e) => {
                e.stopPropagation();
                onToggleStar(email.id);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default GmailEmailList;
